import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Layers, Search, Shield } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { roleOptions as staticRoleOptions } from '../portal/portalConfig';
import { RoundedSelect } from '../components/RoundedSelect';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import { AdminPlatformsTab } from './AdminPlatformsTab';

const USERS_FETCH_SIZE = 200;

export function AdminPage() {
  const { user, refresh } = useAuth();
  const [users, setUsers] = useState(() => {
    const cached = getCachedJson('/api/admin/roles/users');
    if (Array.isArray(cached)) return cached;
    if (cached && Array.isArray(cached.items)) return cached.items;
    return [];
  });
  const [roleOptions, setRoleOptions] = useState(() => getCachedJson('/api/admin/roles/options') || staticRoleOptions);
  const [groups, setGroups] = useState(() => getCachedJson('/api/admin/roles/groups') || []);
  const [stores, setStores] = useState(() => getCachedJson('/api/admin/roles/stores') || []);
  const [isLoading, setIsLoading] = useState(() => (getCachedJson('/api/admin/roles/users') ? false : true));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sort, setSort] = useState({ key: '', dir: null });
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalStep, setRoleModalStep] = useState('role');
  const [adminTab, setAdminTab] = useState('roles');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [selectedRoleCode, setSelectedRoleCode] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const isHkAdmin = useMemo(() => user?.role === 'HK-Admin', [user]);
  const isGeneralUserEntry = useMemo(() => String(selectedUser?.roleCode || '').toUpperCase() === 'GENERAL_USER', [selectedUser]);
  const debouncedSearch = useDebouncedValue(search, 250);
  const usersQueryBase =
    `/api/admin/roles/users?search=${encodeURIComponent(debouncedSearch)}` +
    `&status=${encodeURIComponent(statusFilter === 'All' ? '' : statusFilter)}` +
    `&roleCode=${encodeURIComponent(roleFilter === 'All' ? '' : roleFilter)}`;

  const applyUsersPayload = useCallback((payload) => {
    if (!payload) return;
    if (Array.isArray(payload)) {
      setUsers(payload);
      return;
    }
    if (Array.isArray(payload.items)) {
      setUsers(payload.items);
    }
  }, []);

  const fetchAllUsersPayload = useCallback(async (signal) => {
    const firstPageUrl = `${usersQueryBase}&page=1&pageSize=${encodeURIComponent(String(USERS_FETCH_SIZE))}`;
    const firstPage = await fetchJsonCached(firstPageUrl, { ttlMs: 10_000, signal });
    if (!firstPage || Array.isArray(firstPage)) {
      return firstPage;
    }

    const firstItems = Array.isArray(firstPage.items) ? firstPage.items : [];
    const total = typeof firstPage.total === 'number' ? firstPage.total : firstItems.length;
    if (total <= firstItems.length) {
      return {
        ...firstPage,
        items: firstItems,
        total,
      };
    }

    const totalPages = Math.max(1, Math.ceil(total / USERS_FETCH_SIZE));
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const rest = await Promise.all(
      remainingPages.map((page) =>
        fetchJsonCached(`${usersQueryBase}&page=${page}&pageSize=${encodeURIComponent(String(USERS_FETCH_SIZE))}`, {
          ttlMs: 10_000,
          signal,
        })
      )
    );

    const items = [
      ...firstItems,
      ...rest.flatMap((payload) => (Array.isArray(payload?.items) ? payload.items : [])),
    ];

    return {
      ...firstPage,
      items,
      total,
      page: 1,
      pageSize: items.length,
    };
  }, [usersQueryBase]);

  useEffect(() => {
    if (!isHkAdmin) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();

    const loadStatic = async () => {
      try {
        const [rolesData, groupsData, storesData] = await Promise.all([
          fetchJsonCached('/api/admin/roles/options', { ttlMs: 300_000, signal: controller.signal }),
          fetchJsonCached('/api/admin/roles/groups', { ttlMs: 300_000, signal: controller.signal }),
          fetchJsonCached('/api/admin/roles/stores', { ttlMs: 300_000, signal: controller.signal }),
        ]);
        if (!isCancelled) {
          setRoleOptions(rolesData.length ? rolesData : staticRoleOptions);
          setGroups(groupsData || []);
          setStores(storesData || []);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setError(loadError.message || 'Failed to load role administration data.');
        }
      }
    };

    void loadStatic();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [isHkAdmin]);

  useEffect(() => {
    if (!isHkAdmin) {
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();

    const loadUsers = async () => {
      setIsLoading(true);
      setError('');
      try {
        const usersData = await fetchAllUsersPayload(controller.signal);
        if (!isCancelled) {
          applyUsersPayload(usersData);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setError(loadError.message || 'Failed to load users.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadUsers();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [isHkAdmin, fetchAllUsersPayload, applyUsersPayload]);

  const onOpenRoleModal = (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedRoleCode(targetUser.roleCode);
    setSelectedGroupId(targetUser.groupId ?? null);
    setSelectedStoreId(targetUser.storeId ?? null);
    setRoleModalStep('role');
    setRoleModalOpen(true);
  };

  const onCloseRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedUser(null);
    setSelectedRoleCode('');
    setSelectedGroupId(null);
    setSelectedStoreId(null);
    setRoleModalStep('role');
  };

  const onSaveRole = async () => {
    if (!selectedUser || !selectedRoleCode) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`/api/admin/roles/users/${selectedUser.userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleCode: selectedRoleCode, groupId: selectedGroupId, storeId: selectedStoreId }),
      });

      if (!response.ok) {
        const maybeError = await response.json().catch(() => null);
        throw new Error(maybeError?.message || 'Role update failed.');
      }

      setUsers((current) =>
        current.map((entry) =>
          entry.userId === selectedUser.userId
            ? {
                ...entry,
                roleCode: selectedRoleCode,
                roleLabel: roleOptions.find((role) => role.code === selectedRoleCode)?.label || selectedRoleCode,
                groupId: selectedGroupId,
                storeId: selectedStoreId,
                groupName: groups.find((item) => item.id === selectedGroupId)?.name || null,
                storeName: stores.find((item) => item.id === selectedStoreId)?.name || null,
              }
            : entry
        )
      );
      await refresh();
      setSuccess(`Role for ${selectedUser.email} updated.`);
      onCloseRoleModal();
    } catch (assignError) {
      setError(assignError.message || 'Role update failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const roleRequiresGroup = selectedRoleCode === 'GROUP_ADMIN';
  const roleRequiresStore = selectedRoleCode === 'STORE_ADMIN';

  const onToggleSort = (key) => {
    setSort((current) => {
      if (current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      if (current.dir === 'desc') return { key: '', dir: null };
      return { key, dir: 'asc' };
    });
  };

  const sortedUsers = useMemo(() => {
    if (!sort?.dir || !sort?.key) return users;

    const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
    const toScope = (entry) => entry.storeName || entry.groupName || 'Global';
    const toName = (entry) => [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'No name';

    const valueOf = (entry) => {
      if (sort.key === 'user') return toName(entry);
      if (sort.key === 'email') return entry.email;
      if (sort.key === 'status') return entry.isActive ? 'Active' : 'Inactive';
      if (sort.key === 'role') return entry.roleLabel || entry.roleCode;
      if (sort.key === 'scope') return toScope(entry);
      return '';
    };

    const next = [...users].sort((left, right) => {
      if (sort.key === 'status') {
        const leftRank = left.isActive ? 0 : 1;
        const rightRank = right.isActive ? 0 : 1;
        return leftRank - rightRank;
      }

      return compareText(valueOf(left), valueOf(right));
    });

    return sort.dir === 'desc' ? next.reverse() : next;
  }, [users, sort]);

  if (!isHkAdmin) {
    return (
      <motion.section className="panel-card">
        <h2>Admin access required</h2>
        <p className="hint-text">Only users with HK-Admin role can manage roles.</p>
      </motion.section>
    );
  }

  return (
    <section className="admin-stack">
      <div className="inline-row platform-mode-tabs admin-page-tabs" style={{ gap: 10, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
        <button
          type="button"
          className={adminTab === 'roles' ? 'primary-button admin-tab-button' : 'ghost-button admin-tab-button'}
          onClick={() => setAdminTab('roles')}
        >
          <Shield size={14} />
          Role Management
        </button>
        <button
          type="button"
          className={adminTab === 'platforms' ? 'primary-button admin-tab-button' : 'ghost-button admin-tab-button'}
          onClick={() => setAdminTab('platforms')}
        >
          <Layers size={14} />
          Platforms
        </button>
      </div>

      {adminTab === 'roles' ? (
      <motion.article className="panel-card">
        <div className="panel-title">
          <Shield size={16} />
          <h2>Role Management</h2>
        </div>

        {error && <p className="error-text">{error}</p>}
        {success && <p className="success-text">{success}</p>}

        <div className="admin-filters">
          <div className="search-input-wrap">
            <Search size={14} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by user or email" />
          </div>
          <RoundedSelect
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            options={[
              { value: 'All', label: 'All statuses' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
          <RoundedSelect
            value={roleFilter}
            onChange={(val) => setRoleFilter(val)}
            options={[
              { value: 'All', label: 'All roles' },
              ...roleOptions.map((role) => ({ value: role.code, label: role.label })),
            ]}
          />
        </div>

        {isLoading && users.length === 0 ? (
          <div className="portal-loading-inline">Loading users...</div>
        ) : (
          <div className="table-wrap">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="sortable-th-button" onClick={() => onToggleSort('user')}>
                      User <span className={sort.key === 'user' ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-th-button" onClick={() => onToggleSort('email')}>
                      Email <span className={sort.key === 'email' ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-th-button" onClick={() => onToggleSort('status')}>
                      Status <span className={sort.key === 'status' ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-th-button" onClick={() => onToggleSort('role')}>
                      Role <span className={sort.key === 'role' ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="sortable-th-button" onClick={() => onToggleSort('scope')}>
                      Scope <span className={sort.key === 'scope' ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'} />
                    </button>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((entry) => (
                  <tr key={entry.userId}>
                    <td>{[entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'No name'}</td>
                    <td>{entry.email}</td>
                    <td>{entry.isActive ? 'Active' : 'Inactive'}</td>
                    <td>{entry.roleLabel}</td>
                    <td>{entry.storeName || entry.groupName || 'Global'}</td>
                    <td>
                      {String(entry?.roleCode || '').toUpperCase() !== 'GENERAL_USER' && (
                        <button type="button" className="ghost-button admin-role-save" onClick={() => onOpenRoleModal(entry)}>
                          Change Role
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.article>
      ) : (
      <motion.article className="panel-card admin-platforms-panel">
        <div className="panel-title">
          <Layers size={16} />
          <h2>Platform Catalog</h2>
        </div>
        <AdminPlatformsTab />
      </motion.article>
      )}

      <AnimatePresence mode="wait">
        {roleModalOpen && selectedUser && !isGeneralUserEntry && (
          <motion.div className="modal-backdrop no-dim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="modal-card"
              initial={{ opacity: 0, y: 30, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3>Change Role for {selectedUser.email}</h3>
              {roleModalStep === 'role' ? (
                <div className="modal-role-list">
                  {roleOptions.map((role) => (
                    <button
                      key={role.code}
                      type="button"
                      className={selectedRoleCode === role.code ? 'modal-role-option selected' : 'modal-role-option'}
                      onClick={() => {
                        setSelectedRoleCode(role.code);
                        if (role.code === 'GROUP_ADMIN' || role.code === 'STORE_ADMIN') {
                          setRoleModalStep('scope');
                        } else {
                          setSelectedGroupId(null);
                          setSelectedStoreId(null);
                        }
                      }}
                    >
                      <span>{role.label}</span>
                      {selectedUser.roleCode === role.code && <Check size={14} />}
                    </button>
                  ))}
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}>
                  <p className="hint-text">{roleRequiresStore ? 'Select store for Store-Admin.' : 'Select group for Group-Admin.'}</p>
                  {roleRequiresGroup && (
                    <RoundedSelect
                      value={selectedGroupId || ''}
                      onChange={(val) => setSelectedGroupId(val ? Number(val) : null)}
                      placeholder="Select group"
                      searchable
                      searchPlaceholder="Search groups..."
                      menuMaxHeight={260}
                      options={[
                        { value: '', label: 'Select group' },
                        ...groups.map((group) => ({ value: group.id, label: group.name })),
                      ]}
                    />
                  )}
                  {roleRequiresStore && (
                    <RoundedSelect
                      value={selectedStoreId || ''}
                      onChange={(val) => setSelectedStoreId(val ? Number(val) : null)}
                      placeholder="Select store"
                      searchable
                      searchPlaceholder="Search stores..."
                      menuMaxHeight={260}
                      options={[
                        { value: '', label: 'Select store' },
                        ...stores.map((store) => ({ value: store.id, label: store.name })),
                      ]}
                    />
                  )}
                </motion.div>
              )}

              <div className="inline-row">
                <button type="button" className="ghost-button admin-inline-button" onClick={onCloseRoleModal}>
                  Cancel
                </button>
                {roleModalStep === 'scope' && (
                  <button type="button" className="ghost-button admin-inline-button" onClick={() => setRoleModalStep('role')}>
                    Back
                  </button>
                )}
                <button
                  type="button"
                  className="primary-button admin-inline-button"
                  disabled={isSaving || (roleRequiresGroup && !selectedGroupId) || (roleRequiresStore && !selectedStoreId)}
                  onClick={onSaveRole}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
